import { ArrowLeft, LifeBuoy, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useChamadosControllerListar, useChamadosControllerResumo } from '@/api/generated/suporte/suporte';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { FormularioDeChamado } from '@/features/suporte/components/FormularioDeChamado';
import {
  CHAMADOS_NO_PAINEL,
  INTERVALO_BADGE_MS,
  INTERVALO_PAINEL_MS,
} from '@/features/suporte/suporte.constants';
import {
  CLASSE_STATUS,
  CLASSE_URGENCIA,
  formatarRelativo,
  ROTULO_STATUS,
  ROTULO_URGENCIA,
} from '@/features/suporte/rotulos';
import { cn } from '@/shared/lib/cn';
import { hasPermission } from '@/shared/permissoes/hasPermission';
import { ROTAS, rotaDoChamado } from '@/shared/lib/rotas';
import { Button } from '@/shared/ui/button';

type Modo = 'lista' | 'formulario';

/**
 * Atalho de suporte no canto inferior direito, disponível em qualquer tela.
 *
 * ⚠️ É por causa dele que o `<Toaster>` do sonner foi movido para `top-right`
 * (ver `shared/ui/sonner.tsx`). Os dois no mesmo canto produziam o pior caso
 * possível: algo falha, o toast de erro nasce em cima do botão, e quem quer
 * abrir um chamado por causa daquele erro clica no toast.
 *
 * ⚠️ `z-40`, não `z-50`, de propósito: o overlay da sidebar mobile é `z-40` e o
 * `<aside>` é `z-50`, então com o menu aberto o botão fica coberto — que é o
 * comportamento correto.
 *
 * O painel NÃO usa o `Dialog` do radix: `DialogContent` centraliza na tela com
 * `transform`, e brigar com isso para ancorar no canto custa mais que as
 * poucas linhas de `Escape` + clique-fora aqui.
 */
export function BotaoFlutuanteSuporte() {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [aberto, setAberto] = useState(false);
  const [modo, setModo] = useState<Modo>('lista');
  const painel = useRef<HTMLDivElement>(null);

  const podeVer = hasPermission(me?.permissoes, 'suporte.visualizar');
  const podeAbrir = hasPermission(me?.permissoes, 'suporte.criar');

  // Atalho para a tela em que já se está é ruído — some dentro de /suporte.
  const naTelaDeSuporte = pathname.startsWith(ROTAS.suporte);
  const montado = podeVer && !naTelaDeSuporte;

  const { data: resumo } = useChamadosControllerResumo({
    query: { enabled: montado, refetchInterval: INTERVALO_BADGE_MS },
  });

  const { data: recentes } = useChamadosControllerListar(
    { porPagina: CHAMADOS_NO_PAINEL, ordenarPor: 'abertoEm', ordem: 'desc' },
    // Só busca com o painel ABERTO: a lista curta é detalhe do painel, e
    // mantê-la viva o tempo todo seria um request a cada 5s em toda tela.
    { query: { enabled: montado && aberto && modo === 'lista', refetchInterval: INTERVALO_PAINEL_MS } },
  );

  // Fecha ao navegar — senão o painel sobrevive à mudança de rota e fica
  // flutuando sobre uma tela que não tem nada a ver com ele.
  useEffect(() => {
    setAberto(false);
    setModo('lista');
  }, [pathname]);

  useEffect(() => {
    if (!aberto) return;

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    const aoApontar = (e: PointerEvent) => {
      if (painel.current && !painel.current.contains(e.target as Node)) setAberto(false);
    };

    document.addEventListener('keydown', aoTeclar);
    document.addEventListener('pointerdown', aoApontar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('pointerdown', aoApontar);
    };
  }, [aberto]);

  if (!montado) return null;

  const naoLidas = resumo?.naoLidas ?? 0;

  return (
    <div ref={painel}>
      {aberto && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Suporte"
          className={cn(
            'fixed bottom-24 right-6 z-40 flex max-h-[560px] w-[380px] flex-col overflow-hidden',
            'rounded-xl border-2 border-brand/20 bg-surface-raised shadow-2xl',
            // No celular ocupa a largura toda menos as margens — 380px fixos
            // vazariam da tela.
            'max-sm:inset-x-3 max-sm:w-auto',
          )}
        >
          <div className="flex items-center gap-2 border-b border-brand/10 px-4 py-3">
            {modo === 'formulario' && (
              <button
                type="button"
                onClick={() => setModo('lista')}
                aria-label="Voltar"
                className="text-muted-foreground hover:text-brand"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <p className="flex-1 text-sm font-semibold text-brand">
              {modo === 'lista' ? 'Suporte' : 'Novo chamado'}
            </p>
            <button
              type="button"
              onClick={() => setAberto(false)}
              aria-label="Fechar suporte"
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {modo === 'formulario' ? (
              <FormularioDeChamado
                compacto
                aoCancelar={() => setModo('lista')}
                aoConcluir={(chamado) => {
                  setAberto(false);
                  navigate(rotaDoChamado(chamado.id));
                }}
              />
            ) : (
              <div className="space-y-3">
                {podeAbrir && (
                  <Button className="w-full" onClick={() => setModo('formulario')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Abrir chamado
                  </Button>
                )}

                {recentes?.itens.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Você ainda não tem chamados.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {recentes?.itens.map((chamado) => (
                      <li key={chamado.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setAberto(false);
                            navigate(rotaDoChamado(chamado.id));
                          }}
                          className="w-full rounded-lg border border-brand/15 p-3 text-left transition-colors hover:bg-muted"
                        >
                          <div className="flex items-start gap-2">
                            <span className="flex-1 truncate text-sm font-medium">
                              {chamado.titulo}
                            </span>
                            {chamado.naoLidas > 0 && (
                              <span className="rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold text-brand-foreground">
                                {chamado.naoLidas}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                            <span className={cn('rounded border px-1.5', CLASSE_STATUS[chamado.status])}>
                              {ROTULO_STATUS[chamado.status]}
                            </span>
                            <span className={cn('rounded border px-1.5', CLASSE_URGENCIA[chamado.urgencia])}>
                              {ROTULO_URGENCIA[chamado.urgencia]}
                            </span>
                            <span className="text-muted-foreground">
                              {formatarRelativo(chamado.ultimaMensagemEm ?? chamado.abertoEm)}
                            </span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <button
                  type="button"
                  onClick={() => navigate(ROTAS.suporte)}
                  className="w-full pt-1 text-center text-xs text-brand underline underline-offset-2"
                >
                  Ver todos os chamados
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label="Abrir suporte"
        aria-expanded={aberto}
        className={cn(
          'fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full',
          'bg-brand text-brand-foreground shadow-lg transition-transform hover:scale-105',
        )}
      >
        {aberto ? <X className="h-6 w-6" /> : <LifeBuoy className="h-6 w-6" />}

        {!aberto && naoLidas > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-white">
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </button>
    </div>
  );
}
